interface ChangeLogValue {
  new?: any;
  old?: any;
  newData?: any;
  oldData?: any;
  changeType?: string;
}

interface DataItem {
  [property: string]: ChangeLogValue;
}

interface ChildModule {
  moduleName: string;
  data: {
    newData?: Record<string, any>;
    oldData?: Record<string, any>;
    changeType?: string;
  }[];
}

interface Module {
  moduleName: string;
  data: DataItem[];
  children?: ChildModule[];
}

export interface FormattedLog {
  module: string;
  property: string;
  newValue: string;
  oldValue: string;
  changeDate: string;
  changeType: string;
}

/**
 * Maps module names to their display names
 */
const moduleNameMap: Record<string, string> = {
  'Dim_Projects': 'Project Information',
  'Innoflexphaseoptionsdata': 'Project Information',
  'TechnicalScopeInformation': 'Technical Scope',
  'TechnicalScopeFormat': 'Technical Scope',
  'Fact_Planning_Strategy': 'Planning Strategy',
  'Fact_Planning_Strategy_InHouse_CM': 'Planning Strategy',
  'Fact_Engineering_Scope': 'Engineering Scope',
  'Fact_BcInformation': 'BC Information',
  'Fact_Procurement': 'Procurement',
  'Fact_Sheq_Sustainability': 'Sheq Sustainability',
  'Fact_CMOInnovation_Forum': 'CMOInnovation Forum',
  'Fact_Logistics': 'Logistics',
  'D2D Complete': 'KPI Summary',
  'KPISummary': 'KPI Summary',
  'Milestones': 'Milestones',
  'Dim_ProjectAttributes': 'Project Information'
};

/**
 * Gets the display name for a module, falls back to original name if not mapped
 */
function getModuleDisplayName(moduleName: string): string {
  return moduleNameMap[moduleName] || moduleName;
}

const isIdKey = (key: string) => /(^|_)?id$/i.test(key) || /id$/i.test(key);

/**
 * Removes any key containing "id" (case-insensitive)
 */
const stripIdKeys = (obj: any): any => {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!isIdKey(key)) cleaned[key] = value;
    }
    return cleaned;
  }
  return obj;
};

/**
 * Removes null-valued keys from an object
 */
const removeNullValues = (obj: any): any => {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined) cleaned[key] = value;
    }
    return cleaned;
  }
  return obj;
};

/**
 * Gets the last key from an object (excluding KPIModule and ID keys)
 */
const getLastPropertyKey = (obj: Record<string, any> | undefined | null): string => {
  if (!obj || typeof obj !== 'object') return "-";
  
  const keys = Object.keys(obj);
  if (keys.length === 0) return "-";
  
  // Filter out KPIModule and ID keys, then get the last one
  const filteredKeys = keys.filter(key => 
    key !== "KPIModule" && !isIdKey(key)
  );
  
  return filteredKeys.length > 0 ? filteredKeys[filteredKeys.length - 1] : "-";
};

/**
 * Formats raw change log response data into a flat array of logs.
 */
export function formatChangeLogData(responseData?: Module[]): FormattedLog[] {
  const formattedData: FormattedLog[] = [];

  if (!responseData || !Array.isArray(responseData)) return formattedData;

  responseData.forEach((module) => {
    const displayModuleName = getModuleDisplayName(module.moduleName);

    // Handle main module data
    module.data.forEach((dataItem) => {
      Object.entries(dataItem).forEach(([property, values]) => {
        let newVal = values.new ?? values.newData;
        let oldVal = values.old ?? values.oldData;
        const changeType = values.changeType ?? "-";

        // Remove ID-like keys
        newVal = stripIdKeys(newVal);
        oldVal = stripIdKeys(oldVal);

        // Skip only if truly no data
        if (
          (newVal === null || newVal === undefined || newVal === "") &&
          (oldVal === null || oldVal === undefined || oldVal === "")
        ) {
          return;
        }

        // Fallback: if Planning Strategy has property "-", rename to In-House / CM
        if (displayModuleName === "Planning Strategy" && property === "-") {
          property = "In-House / CM";
        }

        formattedData.push({
          module: displayModuleName,
          property,
          newValue: newVal ? JSON.stringify(newVal) : "-",
          oldValue: oldVal ? JSON.stringify(oldVal) : "-",
          changeDate: new Date().toLocaleString(),
          changeType,
        });
      });
    });

    // Handle child modules
    if (module.children && module.children.length > 0) {
      module.children.forEach((child) => {
        const childDisplayName = getModuleDisplayName(child.moduleName);

        child.data.forEach((childItem) => {
          let newVal = childItem.newData;
          let oldVal = childItem.oldData;
          const changeType = childItem.changeType ?? "-";

          if (
            (!newVal || Object.keys(newVal).length === 0) &&
            (!oldVal || Object.keys(oldVal).length === 0)
          ) {
            return;
          }

          // Clean ID keys
          newVal = stripIdKeys(newVal);
          oldVal = stripIdKeys(oldVal);

          // Special: Planning Strategy → In-House / CM remove nulls
          if (childDisplayName === "Planning Strategy") {
            newVal = removeNullValues(newVal);
            oldVal = removeNullValues(oldVal);
          }

          // KPI Summary Handling - FIXED VERSION
          if (childDisplayName === "KPI Summary") {
            // For KPI Summary, use the last key from the object as property
            // and keep the complete oldData and newData objects
            const property = getLastPropertyKey(newVal || oldVal);
            
            formattedData.push({
              module: childDisplayName,
              property: property,
              newValue: newVal ? JSON.stringify(newVal) : "-",
              oldValue: oldVal ? JSON.stringify(oldVal) : "-",
              changeDate: new Date().toLocaleString(),
              changeType,
            });
          } else {
            let property = Object.keys(newVal || oldVal || {})[0] || "-";

            // Fallback: Planning Strategy with missing property label
            if (childDisplayName === "Planning Strategy" && property === "-") {
              property = "In-House / CM";
            }

            formattedData.push({
              module: childDisplayName,
              property,
              newValue: newVal ? JSON.stringify(newVal) : "-",
              oldValue: oldVal ? JSON.stringify(oldVal) : "-",
              changeDate: new Date().toLocaleString(),
              changeType,
            });
          }
        });
      });
    }
  });

  return formattedData;
}